import React, { useEffect, useState } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Box, Button, Container, Grid, Typography, Chip } from '@mui/material';
// Icons are not used in Hero
import { companyProfile } from '../../data/companyProfile';

const trustItems = [
  { value: '25+', text: 'Years in Business' },
  { value: '$5M/$10M', text: 'Bonding Capacity' },
];

const Hero = () => {
  const { scrollY } = useScroll();
  const y2 = useTransform(scrollY, [0, 300], [0, -20]);

  const words = ['Design', 'Build', 'Integrate', 'Commission'];
  const [wordIndex, setWordIndex] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setWordIndex((prev) => (prev + 1) % words.length), 2500);
    return () => clearInterval(t);
  }, [words.length]);

  const heroImages = [
    {
      src: '/images/hvac-duct.png',
      alt: 'HVAC Ductwork',
      srcSet: '/images/hvac-duct1.png 1280w, /images/hvac-duct.png 1920w',
      sizes: '100vw'
    },
    {
      src: '/images/mech-piping.png',
      alt: 'Mechanical Piping',
      srcSet: '/images/mech-piping1.png 1280w, /images/mech-piping.png 1920w',
      sizes: '100vw'
    }
  ];

  // Background rotation index
  const [bgIndex, setBgIndex] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setBgIndex((prev) => (prev + 1) % heroImages.length);
    }, 9000);
    return () => clearInterval(interval);
  }, [heroImages.length]);

  return (
    <Box
      component="section"
      id="top"
      sx={{
        minHeight: { xs: '100vh', md: '100vh' },
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        overflow: 'hidden',
        pt: { xs: 12, md: 16 },
        pb: { xs: 12, md: 16 },
      }}
    >
      {/* Rotating background layer */}
      <Box aria-hidden sx={{ position: 'absolute', inset: 0, zIndex: 1 }}>
        <AnimatePresence mode="wait">
          <motion.img
            key={heroImages[bgIndex].src}
            src={heroImages[bgIndex].src}
            srcSet={heroImages[bgIndex].srcSet}
            sizes={heroImages[bgIndex].sizes}
            alt=""
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.9 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 2.0, ease: 'easeInOut' }}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', y: y2 }}
            loading="lazy"
            decoding="async"
            fetchPriority="low"
          />
        </AnimatePresence>
        {/* Soft overlay to ensure text contrast over imagery */}
        <Box sx={{ position: 'absolute', inset: 0, background:
          'linear-gradient(180deg, rgba(248,250,252,0.24) 0%, rgba(248,250,252,0.16) 40%, rgba(248,250,252,0.08) 100%)' }} />
      </Box>
      {/* Content overlay above rotating background */}

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid size={{ xs: 12, md: 10, lg: 8 }}>
            <Box sx={{
              p: { xs: 3, md: 4 },
              maxWidth: { xs: '100%', md: 1060, lg: 2040 },
              borderRadius: 3,
              background: 'linear-gradient(90deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.35) 60%, rgba(255,255,255,0.10) 100%)',
              border: '1px solid rgba(25,118,210,0.12)',
              backdropFilter: 'blur(4px)'
            }}>
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: "easeOut" }}
            >
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <Chip 
                  label="NY Metro's Trusted Mechanical Contractor" 
                  sx={{ 
                    mb: 3, 
                    fontWeight: 600,
                    backgroundColor: 'rgba(25,118,210,0.08)',
                    color: 'text.primary',
                    border: '1px solid rgba(25,118,210,0.25)',
                    backdropFilter: 'blur(6px)'
                  }}
                />
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <Typography variant="h1" sx={{ 
                  mb: 2, 
                  color: 'text.primary', 
                  fontWeight: 800,
                  fontSize: { xs: '2.4rem', md: '3.6rem', lg: '4rem' },
                  lineHeight: 1.15
                }}>
                  Full-Service Mechanical
                  <Box component="span" sx={{ display: 'block', color: 'primary.main' }}>
                    <motion.span
                      key={words[wordIndex]}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -12 }}
                      transition={{ duration: 0.5 }}
                      style={{ display: 'inline-block' }}
                    >
                      {words[wordIndex]}
                    </motion.span>
                  </Box>
                </Typography>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                <Typography variant="h6" sx={{ 
                  mb: 4, 
                  color: 'text.secondary', 
                  lineHeight: 1.6,
                  fontSize: { xs: '1.05rem', md: '1.2rem' }
                }}>
                  {companyProfile.companyOverview.summary}
                </Typography>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
              >
                <Box sx={{ display: 'flex', gap: 2, mb: 6, flexWrap: 'wrap' }}>
                  <Button 
                    variant="contained" 
                    size="large" 
                    href="tel:(516) 414-0165"
                    sx={{ 
                      px: 4, 
                      py: 1.5,
                      backgroundColor: 'primary.main',
                      color: 'white',
                      fontWeight: 700,
                      borderRadius: 3
                    }}
                  >
                    Request a Consultation
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="large" 
                    href="#services"
                    sx={{ 
                      px: 4, 
                      py: 1.5,
                      borderColor: 'rgba(25,118,210,0.35)',
                      color: 'text.primary',
                      fontWeight: 700,
                      borderRadius: 3,
                      backdropFilter: 'blur(6px)'
                    }}
                  >
                    Explore Our Services
                  </Button>
                </Box>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1 }}
              >
                <Box sx={{ display: 'flex', gap: { xs: 4, md: 6 }, flexWrap: 'wrap' }}>
                  {trustItems.map((item, index) => (
                    <motion.div
                      key={item.text}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 1.2 + (index * 0.1) }}
                    >
                      <Box>
                        <Typography variant="h4" sx={{ 
                          color: 'text.primary', 
                          fontWeight: 800, 
                          mb: 0.5,
                          fontSize: { xs: '1.5rem', md: '2rem' }
                        }}>
                          {item.value}
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          color: 'text.secondary', 
                          fontWeight: 600,
                          fontSize: '0.9rem'
                        }}>
                          {item.text}
                        </Typography>
                      </Box>
                    </motion.div>
                  ))}
                </Box>
              </motion.div>
            </motion.div>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Hero;
